import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AdminStats } from '../types';
import {
  Users, UserCheck, Shield, AlertTriangle, Heart,
  TrendingUp, DollarSign, Briefcase, BarChart2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';

interface RevenueDataPoint {
  month: string;
  revenue: number;
  users?: number;
}

interface DashboardStatsProps {
  stats: AdminStats;
  chartData?: {
    revenueByMonth?: RevenueDataPoint[];
    [key: string]: any;
  } | null;
}

export function DashboardStats({ stats, chartData }: DashboardStatsProps) {
  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      icon: Users,
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Premium Users',
      value: stats.paidUsers.toLocaleString(),
      icon: UserCheck,
      gradient: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Active Agents',
      value: stats.agents.toLocaleString(),
      icon: Briefcase,
      gradient: 'from-indigo-500 to-indigo-600',
    },
    {
      title: 'Verified Profiles',
      value: stats.verifiedProfiles.toLocaleString(),
      icon: Shield,
      gradient: 'from-green-500 to-green-600',
    },
  ];

  // Pie chart: only include segments with value > 0
  const userDistributionData = [
    { name: 'Free Users', value: stats.freeUsers, color: '#3B82F6' },
    { name: 'Premium Users', value: stats.paidUsers, color: '#A855F7' },
    { name: 'Agents', value: stats.agents, color: '#6366F1' },
  ].filter(d => d.value > 0);

  // Revenue: use real API chart data; no hardcoded fallback
  const revenueData: RevenueDataPoint[] = chartData?.revenueByMonth || chartData?.monthlyRevenue || [];

  // Activity bar chart uses live stats values
  const activityData = [
    { name: 'Verifications', value: stats.verifiedProfiles },
    { name: 'Matches', value: stats.totalMatches },
    { name: 'Success Stories', value: stats.successStories },
    { name: 'Reports', value: stats.totalReports },
  ];

  const hasDistributionData = userDistributionData.some(d => d.value > 0);
  const hasRevenueData = revenueData.length > 0;
  const hasActivityData = activityData.some(d => d.value > 0);

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map(stat => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`h-2 bg-gradient-to-r ${stat.gradient}`} />
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                    <h3 className="text-3xl font-bold text-gray-900">{stat.value}</h3>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg`}>
                    <Icon className="size-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution Pie Chart */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-5 text-purple-600" />
              User Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hasDistributionData ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) =>
                      percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''
                    }
                    outerRadius={100}
                    dataKey="value"
                  >
                    {userDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Users']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                <Users className="size-10 mb-2 opacity-30" />
                <p className="text-sm">No user data available yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Trend — data only from API */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="size-5 text-green-600" />
              Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hasRevenueData ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => [
                      `$${(value / 1000).toFixed(1)}K`,
                      'Revenue',
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10B981"
                    strokeWidth={3}
                    name="Revenue ($)"
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  {revenueData[0]?.users !== undefined && (
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#8B5CF6"
                      strokeWidth={2}
                      name="New Users"
                      dot={{ r: 3 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                <TrendingUp className="size-10 mb-2 opacity-30" />
                <p className="text-sm">Revenue chart data not yet available</p>
                <p className="text-xs mt-1 text-gray-300">
                  Ensure your API returns <code>charts.revenueByMonth</code>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Platform Activity Bar Chart */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          {/* BarChart2 is the lucide icon; BarChart is the recharts component */}
          <CardTitle className="flex items-center gap-2">
            <BarChart2 className="size-5 text-blue-600" />
            Platform Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasActivityData ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Count']} />
                <Bar dataKey="value" fill="#8B5CF6" radius={[8, 8, 0, 0]} name="Count" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
              <BarChart2 className="size-10 mb-2 opacity-30" />
              <p className="text-sm">No activity data available yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-orange-500 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Verifications</p>
                <h3 className="text-3xl font-bold text-orange-600 mt-2">
                  {stats.pendingVerifications}
                </h3>
              </div>
              <AlertTriangle className="size-12 text-orange-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-pink-500 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Matches</p>
                <h3 className="text-3xl font-bold text-pink-600 mt-2">
                  {stats.totalMatches}
                </h3>
              </div>
              <Heart className="size-12 text-pink-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-teal-500 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <h3 className="text-3xl font-bold text-teal-600 mt-2">
                  ${stats.revenue.toLocaleString()}
                </h3>
              </div>
              <DollarSign className="size-12 text-teal-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
